import * as Sentry from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';
import { ENVIRONMENT } from '../env';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  override render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo, showDetails } = this.state;
    const message = error?.message || 'An unexpected error occurred';
    const stack = error?.stack || '';
    const componentStack = errorInfo?.componentStack || '';

    // Inline styles — no dependency on Emotion/styled which may have also crashed
    return (
      <div
        style={{
          minHeight: '100%',
          background: '#1a1a1c',
          color: '#e9ecf5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>:/</div>

          <h1
            style={{
              fontSize: 20,
              fontWeight: 600,
              margin: '0 0 8px',
              lineHeight: 1.4,
            }}
          >
            Something went wrong
          </h1>

          <p
            style={{
              fontSize: 14,
              color: '#8a8f9e',
              margin: '0 0 24px',
              lineHeight: 1.5,
            }}
          >
            {message}
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
            <button
              onClick={this.handleRetry}
              style={{
                height: 40,
                padding: '0 20px',
                borderRadius: 999,
                border: '1px solid #5e84ed',
                background: '#5e84ed',
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={this.handleReload}
              style={{
                height: 40,
                padding: '0 20px',
                borderRadius: 999,
                border: '1px solid #2b2d33',
                background: '#23252a',
                color: '#b7bdcb',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Reload app
            </button>
          </div>

          {ENVIRONMENT === 'DEV' && (
            <>
              <button
                onClick={this.toggleDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#5e84ed',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginBottom: showDetails ? 12 : 0,
                }}
              >
                {showDetails ? 'Hide details' : 'Show details'}
              </button>

              {showDetails && (
                <pre
                  style={{
                    textAlign: 'left',
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: '#8a8f9e',
                    background: '#111214',
                    border: '1px solid #2b2d33',
                    borderRadius: 10,
                    padding: 14,
                    overflowX: 'auto',
                    maxHeight: '40vh',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {stack}
                  {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}
